import { CarouselOptions, SlideDimensions } from './types'

export const calculateSlideDimensions = (
  options: CarouselOptions,
  outerCarouselWrapperWidth: number = 1200,
  carouselScrollWidth: number = 1200
): SlideDimensions => {
  const { slidesPerView, spaceBetween, breakpoints } = options
  let currentSlidesPerView: 'auto' | number = slidesPerView || 'auto'
  let currentSpaceBetween: number = spaceBetween || 16
  // My measurements don't match up with swiper by around 6px. So we will set a offset here.
  const CAROUSEL_MEASUREMENT_OFFSET = 6

  if (breakpoints) {
    const currentWidth = window.innerWidth

    // Sort breakpoints in ascending order (mobile-first approach)
    const sortedBreakpoints = Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b)

    // Find the highest breakpoint that is <= current window width
    // Start with null (meaning use defaults - we're below all breakpoints)
    let activeBreakpoint: number | null = null

    for (const breakPoint of sortedBreakpoints) {
      if (currentWidth >= breakPoint) {
        // This breakpoint is active, keep checking for higher ones
        activeBreakpoint = breakPoint
      } else {
        // We've passed the active breakpoint range, stop here
        break
      }
    }

    // If we found an active breakpoint, apply its settings
    if (activeBreakpoint !== null && breakpoints[activeBreakpoint]) {
      const bpOptions = breakpoints[activeBreakpoint]

      if (bpOptions.slidesPerView !== undefined) currentSlidesPerView = bpOptions.slidesPerView
      if (bpOptions.spaceBetween !== undefined) currentSpaceBetween = bpOptions.spaceBetween
    }

    // Calculate width based on slidesPerView
    if (currentSlidesPerView === 'auto') {
      return {
        width: 'auto',
        marginRight: `${currentSpaceBetween}px`
      }
    } else {
      const totalSpacing = Number(currentSlidesPerView) * Number(currentSpaceBetween)
      const offsetScrollWidth = carouselScrollWidth - outerCarouselWrapperWidth
      const slideWidth = (carouselScrollWidth - totalSpacing - offsetScrollWidth) / Number(currentSlidesPerView)

      return {
        width: `${Math.round(slideWidth * 100) / 100 + CAROUSEL_MEASUREMENT_OFFSET}px`,
        marginRight: `${currentSpaceBetween}px`
      }
    }
  } else {
    // no breakpoints found, calculate based on default slidesPerView
    if (currentSlidesPerView === 'auto') {
      return {
        width: 'auto',
        marginRight: `${currentSpaceBetween}px`
      }
    } else {
      const totalSpacing = Number(currentSlidesPerView) * Number(currentSpaceBetween)
      const offsetScrollWidth = carouselScrollWidth - outerCarouselWrapperWidth
      const slideWidth = (carouselScrollWidth - totalSpacing - offsetScrollWidth) / Number(currentSlidesPerView)

      return {
        width: `${Math.round(slideWidth * 100) / 100 + CAROUSEL_MEASUREMENT_OFFSET}px`,
        marginRight: `${currentSpaceBetween}px`
      }
    }
  }
}

export const getCurrentBreakPointOption = (options: CarouselOptions, currentWidth: number) => {
  const breakpoints = options.breakpoints

  if (!breakpoints) {
    return null
  }

  // Sort breakpoints in ascending order (mobile-first approach)
  const sortedBreakpoints = Object.keys(breakpoints)
    .map(Number)
    .sort((a, b) => a - b)

  // Find the highest breakpoint that is <= current window width
  // Start with null (meaning use defaults - we're below all breakpoints)
  let activeBreakpoint: number | null = null

  for (const breakPoint of sortedBreakpoints) {
    if (currentWidth >= breakPoint) {
      // This breakpoint is active, keep checking for higher ones
      activeBreakpoint = breakPoint
    } else {
      // We've passed the active breakpoint range, stop here
      break
    }
  }

  if (activeBreakpoint !== null && breakpoints[activeBreakpoint]) {
    return breakpoints[activeBreakpoint]
  } else {
    return {
      slidesPerView: options.slidesPerView,
      spaceBetween: options.spaceBetween
    }
  }
}

// Reusable calculator that reads DOM, computes dimensions, and returns the values
export function computeCarouselDimensions(params: {
  options: CarouselOptions
  carouselSelector: string
  outerCarouselWrapperSelector: string
}): (SlideDimensions & { marginBottom?: string }) | null {
  const { options, carouselSelector, outerCarouselWrapperSelector } = params

  const carouselElement = document.querySelector(
    `.carousel-service, .carousel, [data-selector="${carouselSelector}"]`
  ) as HTMLElement | null
  const outerCarouselWrapperElement = document.querySelector(
    `.${outerCarouselWrapperSelector}`
  ) as HTMLElement | null

  if (!outerCarouselWrapperElement || !carouselElement) {
    return null
  }

  const carouselElementScrollWidth = carouselElement.scrollWidth
  const dimensions = calculateSlideDimensions(
    options,
    outerCarouselWrapperElement.clientWidth,
    carouselElementScrollWidth
  ) as SlideDimensions & { marginBottom?: string }

  const currentBreakPointOption = getCurrentBreakPointOption(options, window.innerWidth)

  if (currentBreakPointOption?.spaceBetween !== undefined) {
    dimensions.marginBottom = `${currentBreakPointOption.spaceBetween}px`
  }

  return dimensions
}
